import { CdnEnv, configUrl, isConfigEnvelope } from "@/cdn/envelope";
import { pluginConfig } from "@/globals/pluginConfig";

// Default true: the plugin path has no siteId to validate (config is
// server-injected by PHP), and legit CDN pro sites must not flash branding
// while validation is in flight. Only a *failed* pro validation revokes.
let entitled = true;

export function isEntitled(): boolean {
  return entitled;
}

export function _resetForTests(): void {
  entitled = true;
}

function domainMatches(hostname: string, domains: string[]): boolean {
  const strip = (h: string) => h.replace(/^www\./i, "").toLowerCase();
  const host = strip(hostname);
  return domains.some((d) => strip(d) === host);
}

/**
 * Pro-core runtime validation (spec §8): never trust the handed-off config.
 * Re-fetch the published config (same URL string as the loader → browser
 * cache hit) and verify plan + domain. Fail-open on network errors.
 */
export async function validateEntitlement(
  env: CdnEnv,
  fetchImpl: typeof fetch = fetch,
  hostname: string = location.hostname
): Promise<boolean> {
  if (BUILD_TIER === "pro") {
    try {
      const res = await fetchImpl(configUrl(env.cdnBase, env.siteId));
      if (!res.ok) return entitled; // fail-open
      const body: unknown = await res.json();
      if (!isConfigEnvelope(body)) return entitled; // fail-open
      const valid = body.plan === "pro" && domainMatches(hostname, body.domains);
      if (!valid) {
        entitled = false;
        applyBrandingState();
      }
      return valid;
    } catch {
      return entitled; // fail-open
    }
  }
  return true;
}

/**
 * Applies the branding decision to the menu footer. Callable any time:
 * before the menu exists (no-op), at menu render, and after validation.
 * Hides only the branding slot; the footer itself collapses only when the
 * statement link is hidden too, so removing branding never removes the
 * accessibility-statement entry (they share the footer since the redesign).
 */
export function applyBrandingState(): void {
  const footer = document.querySelector("#cya11y-container")?.shadowRoot
    ?.querySelector(".cya11y-footer") as HTMLElement | null;
  if (!footer) return;
  const brand = footer.querySelector(".cya11y-footer-brand") as HTMLElement | null;
  const statement = footer.querySelector(".cya11y-accessibility-statement-link") as HTMLElement | null;
  const hideBrand = BUILD_TIER === "pro" && pluginConfig.hideBranding === true && entitled;
  if (brand) brand.style.display = hideBrand ? "none" : "";
  const statementVisible = !!statement && statement.style.display !== "none";
  footer.style.display = hideBrand && !statementVisible ? "none" : "";
}
